'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  useCancelSubscriptionMutation,
  usePayments,
  useSubscription,
  useTeamSubscription,
} from '../../hooks/queries/useBilling';
import { useTeamMembers, useTeams } from '../../hooks/queries/useTeams';
import { useWorkspaceScope } from '../../hooks/useWorkspaceScope';
import { useUIStore } from '../../stores/uiStore';
import { getAuthSession, saveAuthSession } from '../../lib/authSession';
import { cn } from '../../lib/cn';
import type { PaymentCurrency, PaymentStatus, SubscriptionPlan, SubscriptionStatus } from '../../lib/api';
import { formatLocalDate } from '../../lib/dateTime';
import { BaseModal } from '../BaseModal';
import { Select } from '../Select';
import { Spinner } from '../loading/Spinner';

// `subscription.plan`은 서버 enum이라 그대로 찍으면 `PERSONAL_PRO`가 사용자에게 노출된다.
// 표시용 라벨은 billing.* 에 이미 존재하므로 enum → 라벨 키만 매핑한다.
const planLabelKey: Record<SubscriptionPlan, string> = {
  FREE: 'freePlan',
  PRO: 'proPlan',
  PERSONAL_PRO: 'personalProPlan',
  TEAM_PRO: 'teamProPlan',
};

// 필드 카드: `bg-base-200/50`은 라이트(base-100=흰색) 위에서 사실상 흰색이라 카드가 사라졌다.
// 면은 elevated + 실선 테두리로 구분한다. sunken 틴트를 쓰면 그 위의 muted 라벨이
// 라이트에서 4.34:1로 AA에 못 미쳐, 라벨 색을 앱 전반 패턴에서 벗어나게 바꿔야 한다.
const fieldCellStyle = { background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' };
const fieldLabelStyle = { color: 'var(--color-fg-muted)' };

const statusBadgeClass: Record<SubscriptionStatus, string> = {
  ACTIVE: 'badge-success',
  TRIALING: 'badge-info',
  CANCELLED: 'badge-warning',
  PAST_DUE: 'badge-error',
  EXPIRED: 'badge-ghost',
};

const paymentStatusBadgeClass: Record<PaymentStatus, string> = {
  PENDING: 'badge-warning',
  PAID: 'badge-success',
  FAILED: 'badge-error',
};

const formatCurrency = (amount: number, currency: PaymentCurrency) => {
  if (currency === 'KRW') {
    return `₩${amount.toLocaleString()}`;
  }
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '–';
  return formatLocalDate(dateString);
};

type SubscriptionTab = 'personal' | 'team';

const subscriptionTabs = ['personal', 'team'] as const satisfies readonly SubscriptionTab[];

export const SubscriptionModal = () => {
  const t = useTranslations('billing.subscription');
  const tCommon = useTranslations('common');
  const tPayments = useTranslations('billing.payments');
  const tBilling = useTranslations('billing');
  const { token, session } = useWorkspaceScope();

  const open = useUIStore((s) => s.isSubscriptionModalOpen);
  const target = useUIStore((s) => s.subscriptionModalTarget);
  const closeModal = useUIStore((s) => s.closeSubscriptionModal);
  const queryToken = open ? token : null;

  const [activeTab, setActiveTab] = useState<SubscriptionTab>(target.mode);
  const [selectedTeamId, setSelectedTeamId] = useState(target.mode === 'team' ? target.teamId : '');
  const [page, setPage] = useState(1);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isTeamMode = activeTab === 'team';
  const teamId = isTeamMode && selectedTeamId.length > 0 ? selectedTeamId : null;
  const { data: teams = [], isLoading: loadingTeams } = useTeams(queryToken);
  const selectedTeam = teams.find((team) => team.id === selectedTeamId) ?? null;
  const selectedTeamName =
    selectedTeam?.name ?? (target.mode === 'team' && target.teamId === selectedTeamId ? target.teamName : '');
  const { data: personalSubscription, isLoading: loadingPersonalSub } = useSubscription(queryToken);
  const { data: teamSubscription, isLoading: loadingTeamSub } = useTeamSubscription(queryToken, teamId);
  const { data: teamMembers = [], isLoading: loadingTeamMembers } = useTeamMembers(queryToken, teamId);
  const subscription = isTeamMode ? teamSubscription : personalSubscription;
  const loadingSub = isTeamMode ? loadingTeamSub : loadingPersonalSub;
  const cancelMutation = useCancelSubscriptionMutation(token);

  const { data: paymentsResult, isLoading: loadingPayments } = usePayments(isTeamMode && !teamId ? null : queryToken, {
    page,
    limit: 10,
    teamId,
  });

  const isSelectedTeamOwner = useMemo(() => {
    if (!session?.member.id || !teamId) {
      return false;
    }
    return teamMembers.some((member) => member.memberId === session.member.id && member.role === 'OWNER');
  }, [session?.member.id, teamId, teamMembers]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveTab(target.mode);
    setSelectedTeamId(target.mode === 'team' ? target.teamId : '');
    setPage(1);
    setShowCancelConfirm(false);
  }, [open, target]);

  useEffect(() => {
    if (!open || activeTab !== 'team' || selectedTeamId.length > 0 || teams.length === 0) {
      return;
    }

    setSelectedTeamId(teams[0].id);
  }, [activeTab, open, selectedTeamId, teams]);

  const switchTab = (tab: SubscriptionTab) => {
    setActiveTab(tab);
    setPage(1);
    setShowCancelConfirm(false);
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({ teamId });
      const session = getAuthSession();
      if (!isTeamMode && session) {
        saveAuthSession({
          ...session,
          member: { ...session.member, tier: 'FREE' },
        });
      }
      toast.success(t('cancelled'));
      setShowCancelConfirm(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel');
    }
  };

  const hasActiveSub = subscription && subscription.status !== 'EXPIRED' && subscription.status !== 'CANCELLED';
  const canCancelSubscription = !isTeamMode || isSelectedTeamOwner;
  const isTeamUnavailable = isTeamMode && !loadingTeams && teams.length === 0;
  const isTeamSelectionPending = isTeamMode && !teamId && (loadingTeams || teams.length > 0);

  return (
    <BaseModal
      isOpen={open}
      onClose={() => {
        setShowCancelConfirm(false);
        closeModal();
      }}
      variant="bottomSheet"
      size="2xl"
    >
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-base font-semibold">{t('manageTitle')}</h3>
        <button
          type="button"
          aria-label="Close"
          className="btn btn-ghost btn-circle btn-sm"
          onClick={() => {
            setShowCancelConfirm(false);
            closeModal();
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div role="tablist" aria-label={t('manageTitle')} className="mb-5 grid grid-cols-2 rounded-xl bg-base-200 p-1">
        {subscriptionTabs.map((tab) => (
          <button
            key={tab}
            id={`subscription-tab-${tab}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`subscription-tabpanel-${tab}`}
            className={cn(
              'btn btn-ghost btn-sm min-h-9 rounded-lg border-0 px-2 text-xs font-medium sm:px-3 sm:text-sm',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              activeTab === tab && 'bg-base-100 text-primary shadow-xs',
            )}
            onClick={() => switchTab(tab)}
          >
            {t(`${tab}Tab`)}
          </button>
        ))}
      </div>

      {isTeamMode && teams.length > 0 && (
        <div className="mb-5 rounded-xl border border-base-300 bg-base-100 p-4">
          <label className="mb-2 block text-xs font-medium" style={fieldLabelStyle} htmlFor="subscription-team-select">
            {t('teamSelect')}
          </label>
          <Select
            id="subscription-team-select"
            size="sm"
            fullWidth
            value={selectedTeamId}
            onChange={(value) => {
              setSelectedTeamId(value);
              setPage(1);
              setShowCancelConfirm(false);
            }}
            options={teams.map((team) => ({ value: team.id, label: team.name }))}
          />
        </div>
      )}

      <div id={`subscription-tabpanel-${activeTab}`} role="tabpanel" aria-labelledby={`subscription-tab-${activeTab}`}>
        {isTeamUnavailable ? (
          <div className="rounded-xl border-2 border-dashed border-base-300 py-10 text-center">
            <p className="font-medium">{t('noTeams')}</p>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('noTeamsDescription')}</p>
          </div>
        ) : loadingSub || isTeamSelectionPending || (isTeamMode && (loadingTeams || loadingTeamMembers)) ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : subscription && subscription.status ? (
          <>
            <section className="rounded-xl border border-base-300 bg-base-100 p-4">
              {/* 카드 헤더를 두지 않는다 — 대상(개인/팀)은 탭과 팀 선택 컨트롤이 이미 말하고,
                  상태는 플랜 셀에 배지로 붙는다. 헤더를 두면 같은 정보가 두 번 나온다. */}
              {/* 셀 수가 팀 4개 / 개인 3개라, 마지막 줄이 비지 않도록 열 수를 나눈다. */}
              <div className={cn('grid gap-3', isTeamMode ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
                <div className="rounded-lg border p-3" style={fieldCellStyle}>
                  <p className="text-xs" style={fieldLabelStyle}>
                    {t('plan')}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{tBilling(planLabelKey[subscription.plan])}</p>
                    <span className={cn('badge badge-sm', statusBadgeClass[subscription.status])}>
                      {t(`statusLabel.${subscription.status}`)}
                    </span>
                  </div>
                </div>
                {isTeamMode && (
                  <div className="rounded-lg border p-3" style={fieldCellStyle}>
                    <p className="text-xs" style={fieldLabelStyle}>
                      {t('seats')}
                    </p>
                    <p className="mt-1 font-semibold">{subscription.seats}</p>
                  </div>
                )}
                <div className="rounded-lg border p-3" style={fieldCellStyle}>
                  <p className="text-xs" style={fieldLabelStyle}>
                    {t('paymentMethod')}
                  </p>
                  <p className="mt-1 text-sm">{tBilling(`provider.${subscription.provider}`)}</p>
                </div>
                <div className="rounded-lg border p-3" style={fieldCellStyle}>
                  <p className="text-xs" style={fieldLabelStyle}>
                    {subscription.status === 'TRIALING'
                      ? t('trialEnds')
                      : subscription.status === 'CANCELLED'
                        ? t('expiresAt')
                        : t('nextPayment')}
                  </p>
                  <p className="mt-1 text-sm">
                    {subscription.status === 'TRIALING'
                      ? formatDate(subscription.trialEndAt)
                      : formatDate(subscription.currentPeriodEndAt)}
                  </p>
                </div>
              </div>
            </section>

            {hasActiveSub && canCancelSubscription && (
              <div className="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                {!showCancelConfirm ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-[var(--color-fg-muted)]">{t('cancelHelp')}</p>
                    <button
                      type="button"
                      className="btn btn-outline btn-error btn-sm"
                      onClick={() => setShowCancelConfirm(true)}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-[var(--color-fg-muted)]">{t('cancelConfirm')}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-error btn-sm"
                        disabled={cancelMutation.isPending}
                        onClick={handleCancel}
                      >
                        {cancelMutation.isPending ? <Spinner /> : t('cancelConfirmAction')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        {t('cancelConfirmDismiss')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {hasActiveSub && !canCancelSubscription && (
              <p className="mt-4 rounded-xl border border-base-300 bg-base-100 p-4 text-sm text-[var(--color-fg-muted)]">
                {t('ownerOnlyCancel')}
              </p>
            )}

            {/* Payment history */}
            <section className="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
              <h4 className="text-sm font-semibold">{tPayments('title')}</h4>
              {loadingPayments ? (
                <div className="mt-4 flex justify-center">
                  <Spinner size="sm" />
                </div>
              ) : !paymentsResult?.data.length ? (
                <p className="mt-2 text-xs" style={fieldLabelStyle}>
                  {tPayments('noPayments')}
                </p>
              ) : (
                <>
                  <div className="mt-3 overflow-x-auto rounded-lg border border-base-300">
                    <table className="table table-xs">
                      <thead>
                        <tr>
                          <th>{tPayments('date')}</th>
                          <th>{tPayments('amount')}</th>
                          <th>{tPayments('provider')}</th>
                          <th>{tCommon('fields.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentsResult.data.map((payment) => (
                          <tr key={payment.id}>
                            <td className="text-xs">{formatDate(payment.paidAt ?? payment.createdAt)}</td>
                            <td className="text-xs font-medium">{formatCurrency(payment.amount, payment.currency)}</td>
                            <td className="text-xs">{tBilling(`provider.${payment.provider}`)}</td>
                            <td>
                              <span className={cn('badge badge-xs', paymentStatusBadgeClass[payment.status])}>
                                {tPayments(`statusLabel.${payment.status}`)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {paymentsResult.meta.totalPages > 1 && (
                    <div className="mt-2 flex justify-center gap-2">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        &larr;
                      </button>
                      <span className="flex items-center text-xs text-[var(--color-fg-subtle)]">
                        {page} / {paymentsResult.meta.totalPages}
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page >= paymentsResult.meta.totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        &rarr;
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        ) : (
          <div className="rounded-xl border-2 border-dashed border-base-300 py-10 text-center">
            <p className="font-medium">{t('noPlan')}</p>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              {isTeamMode ? t('teamNoPlanDescription') : t('noPlanDescription')}
            </p>
            {(!isTeamMode || teamId) && (
              <button
                type="button"
                className="btn btn-primary btn-sm mt-4"
                onClick={() => {
                  closeModal();
                  useUIStore
                    .getState()
                    .openSubscribeModal(
                      isTeamMode
                        ? { mode: 'team', teamId: teamId as string, teamName: selectedTeamName }
                        : { mode: 'personal' },
                    );
                }}
              >
                {t('upgradeCta')}
              </button>
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
};
